import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  apiClient,
  getAccessToken,
  REFRESH_TOKEN_STORAGE_KEY,
  registerRefreshFailureHandler,
  setAccessToken,
} from '@/lib/apiClient';
import { wsClient } from '@/lib/wsClient';
import type { AuthUser, LoginResponse } from './types';

export interface LoginArgs {
  email: string;
  password: string;
  totpCode?: string;
  backupCode?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (args: LoginArgs) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchMe(): Promise<AuthUser> {
  const res = await apiClient.get<AuthUser>('/api/web/v1/auth/me');
  return res.data;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    setUser(null);
    wsClient.disconnect();
  }, []);

  useEffect(() => {
    registerRefreshFailureHandler(clearSession);
    return () => registerRefreshFailureHandler(null);
  }, [clearSession]);

  useEffect(() => {
    const storedRefresh = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (!storedRefresh) {
      setLoading(false);
      return;
    }
    apiClient
      .post<LoginResponse>('/api/web/v1/auth/refresh', { refreshToken: storedRefresh })
      .then(async (res) => {
        setAccessToken(res.data.accessToken);
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, res.data.refreshToken);
        const me = await fetchMe();
        setUser(me);
        wsClient.connect(getAccessToken);
      })
      .catch(() => clearSession())
      .finally(() => setLoading(false));
  }, [clearSession]);

  const login = useCallback(async ({ email, password, totpCode, backupCode }: LoginArgs) => {
    const res = await apiClient.post<LoginResponse>('/api/web/v1/auth/login', {
      email,
      password,
      totpCode,
      backupCode,
    });
    setAccessToken(res.data.accessToken);
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, res.data.refreshToken);
    const me = await fetchMe();
    setUser(me);
    wsClient.connect(getAccessToken);
  }, []);

  const logout = useCallback(async () => {
    const storedRefresh = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (storedRefresh) {
      try {
        await apiClient.post('/api/web/v1/auth/logout', { refreshToken: storedRefresh });
      } catch {
        // Ignore logout failures — we clear local state regardless.
      }
    }
    clearSession();
  }, [clearSession]);

  const refreshUser = useCallback(async () => {
    const me = await fetchMe();
    setUser(me);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, logout, refreshUser }),
    [user, loading, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
